import { useEffect, useMemo, useState } from 'react'
import { perfilParaEstado, pontoDaAbertura, volumeTanqueM3 } from '../../../dominio/caminhaoTanque/perfil'
import { produtoPorId, type IdProduto } from '../../../dominio/produtos'
import {
  POSES,
  alturaEixoNaPose,
  nivelAcimaDoEixoM,
  pontoGirado,
  situacaoDasAberturas,
  type IdPose,
  type PoseTanque,
} from '../../../dominio/transbordo/pose'
import { avaliarPosicaoReceptor, COMPRIMENTO_MANGOTE_M } from '../../../dominio/transbordo/posicionamento'
import { ACOES, acaoPorId, avaliarProcedimento, type PassoDoAluno } from '../../../dominio/transbordo/procedimento'
import { pontoCardeal } from '../../../dominio/vento'
import { CaminhaoTanque3D, chaveParte, type ParteCaminhao } from '../../../visualizacao3d/caminhaoTanque/CaminhaoTanque3D'
import type { VolumeEnquadramento } from '../../../visualizacao3d/camera/comandosCamera'
import { Biruta3D } from '../../../visualizacao3d/cenario/Biruta3D'
import { Pista3D } from '../../../visualizacao3d/cenario/Pista3D'
import { Terreno3D } from '../../../visualizacao3d/cenario/Terreno3D'
import { Rotulo3D } from '../../../visualizacao3d/componentes/Rotulo3D'
import { Cabo3D, Faisca3D, HasteAterramento3D, MarcadorFase3D } from '../../../visualizacao3d/transbordo/Conexoes3D'
import { ReceptorAnimado3D } from '../../../visualizacao3d/transbordo/ReceptorAnimado3D'
import { IconeCena, IconeModuloTransbordo, IconePosicao, IconeProcedimentos } from '../../icones'
import type { ConteudoInspetor } from '../../modulos/tipos'
import { useHistoricoDeModulo } from '../../useHistoricoDeModulo'
import { CascaOficina, type ContextoCasca } from '../../workspace/CascaOficina'
import { RosaDosVentos } from '../comum/RosaDosVentos'
import { SeletorProduto } from '../comum/SeletorProduto'
import { BotoesProcedimento, RegistroProcedimento } from './PainelProcedimento'

const VOLUME: VolumeEnquadramento = { comprimento: 30, largura: 24, altura: 5, centro: [4, -2] }
/** Únicas ações cujo erro é especificamente risco de faísca por eletricidade estática (ver procedimento.ts). */
const ACOES_RISCO_ELETRICO = new Set<PassoDoAluno['acao']>(['conectarMangoteLiquido', 'conectarRetornoVapor', 'equipotencializar'])
/** Avariado parado no acostamento direito, frente para +Z. */
const AVARIADO: readonly [number, number] = [1.75, 0]
const fmt = (v: number, casas = 1) => v.toLocaleString('pt-BR', { maximumFractionDigits: casas, minimumFractionDigits: casas })

type Ponto = readonly [number, number, number]

/**
 * Oficina de transbordo: a pose do tanque decide em que fase cada abertura
 * está; o aluno posiciona o receptor, aterra, equipotencializa, conecta e
 * transfere — e cada passo é conferido contra a ordem segura.
 */
export function OficinaTransbordo({ contexto }: { contexto: ContextoCasca }) {
  const { executar: executarCamera } = contexto.camera
  useEffect(() => { executarCamera('restaurar') }, [executarCamera])
  const [idProduto, setIdProduto] = useState<IdProduto>('glp')
  const [enchimento, setEnchimento] = useState(0.8)
  const [pose, setPose] = useState<PoseTanque>({ pose: 'tombadoDireito', arfagemGraus: 0 })
  const [opacidade, setOpacidade] = useState(0.35)
  const [direcaoVento, setDirecaoVento] = useState(90)
  const [receptor, setReceptor] = useState<readonly [number, number]>([9.5, 1])
  const [posicionando, setPosicionando] = useState(false)
  const [passos, setPassos] = useState<PassoDoAluno[]>([])
  const [demonstrando, setDemonstrando] = useState(false)
  const [reproduzindo, setReproduzindo] = useState(false)
  const [parte, setParte] = useState<ParteCaminhao | null>(null)
  const [faisca, setFaisca] = useState(false)

  const historico = useHistoricoDeModulo(
    () => ({ passos, receptor, pose, enchimento, direcaoVento }),
    (valor) => {
      setDemonstrando(false)
      setReproduzindo(false)
      setPose(valor.pose)
      setEnchimento(valor.enchimento)
      setDirecaoVento(valor.direcaoVento)
      setPassos(valor.passos)
      setReceptor(valor.receptor)
    },
  )

  const produto = produtoPorId(idProduto)
  const perfil = useMemo(() => perfilParaEstado(produto.estadoTransporte), [produto.estadoTransporte])
  const { tanque } = perfil
  const rolagem = POSES[pose.pose].rolagemGraus
  const eixoY = alturaEixoNaPose(tanque, pose)
  const nivelMundoY = eixoY + nivelAcimaDoEixoM(tanque, rolagem, enchimento)
  const situacoes = situacaoDasAberturas(tanque, pose, enchimento)
  const posicao = avaliarPosicaoReceptor(AVARIADO, receptor, direcaoVento)
  const avaliacao = avaliarProcedimento(passos, {
    estado: produto.estadoTransporte,
    tanque,
    pose,
    enchimento,
    receptorAFavorDoVento: posicao.setor === 'aFavor',
  })
  // faísca visual: reage ao erro de aterramento/equipotencialização que avaliarProcedimento já detecta
  useEffect(() => {
    const ultimo = avaliacao.passos.at(-1)
    if (!ultimo || ultimo.correto || !ACOES_RISCO_ELETRICO.has(ultimo.passo.acao)) return
    setFaisca(true)
    const t = window.setTimeout(() => setFaisca(false), 500)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passos.length])
  const poseDemo = useMemo<PoseTanque>(() => ({
    pose: produto.estadoTransporte === 'liquido' ? 'tombadoDireito' : 'sobreRodas',
    arfagemGraus: 0,
  }), [produto.estadoTransporte])
  const sequenciaDemo = useMemo(() => {
    const aberturas = situacaoDasAberturas(tanque, poseDemo, 0.8)
    return ACOES.filter((a) => !a.somenteGasLiquefeito || produto.estadoTransporte === 'gasLiquefeitoPressurizado').map((a): PassoDoAluno => ({
      acao: a.id,
      idAbertura: a.usaAbertura ? aberturas.find((s) => !s.obstruida && s.fase === a.usaAbertura && s.abertura.tipo !== 'valvulaAlivio' && s.abertura.tipo !== 'bocaVisita')?.abertura.id : undefined,
    }))
  }, [tanque, produto.estadoTransporte, poseDemo])
  const avancarDemo = () => setPassos((atual) => sequenciaDemo.slice(0, Math.min(sequenciaDemo.length, atual.length + 1)))
  useEffect(() => {
    if (!demonstrando || !reproduzindo || passos.length >= sequenciaDemo.length) return
    const timer = window.setTimeout(() => setPassos(sequenciaDemo.slice(0, passos.length + 1)), 3500)
    return () => window.clearTimeout(timer)
  }, [demonstrando, reproduzindo, passos.length, sequenciaDemo])
  const iniciarDemo = () => {
    if (!demonstrando) historico.registrar()
    setPose(poseDemo)
    setEnchimento(0.8)
    setDirecaoVento(90)
    setReceptor([9.5, 1])
    setPosicionando(false)
    setPassos(sequenciaDemo.slice(0, 1))
    setDemonstrando(true)
    setReproduzindo(true)
  }
  const pausado = demonstrando && !reproduzindo
  const feitas = new Set(avaliacao.passos.filter((p) => p.correto).map((p) => p.passo.acao))
  const passoCorreto = (acao: PassoDoAluno['acao']) => avaliacao.passos.find((p) => p.correto && p.passo.acao === acao)?.passo

  const noMundoAvariado = (id: string): Ponto | null => {
    const abertura = tanque.aberturas.find((a) => a.id === id)
    if (!abertura) return null
    const [x, y, z] = pontoGirado(tanque, abertura, pose)
    return [AVARIADO[0] + x, eixoY + y, AVARIADO[1] + tanque.zCentroM + z]
  }
  const noMundoReceptor = (tipo: 'liquido' | 'vapor'): Ponto => {
    const abertura =
      tanque.aberturas.find((a) => (tipo === 'liquido' ? a.tipo === 'valvulaFaseLiquida' || a.tipo === 'valvulaFundo' : a.tipo === 'valvulaFaseVapor')) ??
      tanque.aberturas[0]!
    const [x, y, z] = pontoDaAbertura(tanque, abertura)
    return [receptor[0] + x, y, receptor[1] + z]
  }

  const executar = (passo: PassoDoAluno) => {
    historico.registrar()
    setPassos((atual) => [...atual, passo])
  }
  const reiniciar = () => {
    setDemonstrando(false)
    setReproduzindo(false)
    historico.registrar()
    setPassos([])
  }

  const haste = (centro: readonly [number, number]): readonly [number, number] => [centro[0] + 3, centro[1] + 3]
  const chassi = (centro: readonly [number, number], y = 0.9): Ponto => [centro[0] + 1.05, y, centro[1] - 1]

  // Mesmo pivô e mesma rotação do grupo 3D: o grampo acompanha o chassi tombado.
  const r = rolagem * Math.PI / 180
  const inclinacao = pose.arfagemGraus * Math.PI / 180
  const yChassi = 0.9 - tanque.alturaEixoM
  const zChassi = -1 - tanque.zCentroM
  const xGirado = 1.05 * Math.cos(r) - yChassi * Math.sin(r)
  const yGirado = 1.05 * Math.sin(r) + yChassi * Math.cos(r)
  const chassiAvariado: Ponto = [AVARIADO[0] + xGirado, eixoY + yGirado * Math.cos(inclinacao) - zChassi * Math.sin(inclinacao), AVARIADO[1] + tanque.zCentroM + yGirado * Math.sin(inclinacao) + zChassi * Math.cos(inclinacao)]

  const situacaoSelecionada = parte?.tipo === 'abertura' ? situacoes.find((s) => s.abertura.id === parte.id) : null
  const inspetor: ConteudoInspetor | null = situacaoSelecionada
    ? {
        titulo: situacaoSelecionada.abertura.nome,
        subtitulo: situacaoSelecionada.obstruida ? 'Prensada contra o solo' : situacaoSelecionada.fase === 'liquido' ? 'Em fase líquida' : 'Em fase vapor',
        propriedades: [
          { rotulo: 'Fase nesta pose', valor: situacaoSelecionada.fase === 'liquido' ? 'líquido' : 'vapor' },
          {
            rotulo: situacaoSelecionada.profundidadeM > 0 ? 'Coluna de líquido acima' : 'Distância até o líquido',
            valor: `${fmt(Math.abs(situacaoSelecionada.profundidadeM), 2)} m`,
          },
          { rotulo: 'Altura do chão', valor: `${fmt(situacaoSelecionada.alturaSoloM, 2)} m${situacaoSelecionada.obstruida ? ' — inacessível' : ''}` },
          { rotulo: 'Para que serve', valor: situacaoSelecionada.abertura.descricao },
        ],
        aoFechar: () => setParte(null),
      }
    : null

  const grupos = [
    {
      id: 'cenario',
      rotulo: 'Cenário',
      icone: <IconeModuloTransbordo />,
      resumo: `${produto.nome}, ${POSES[pose.pose].nome.toLowerCase()}`,
      conteudo: () => (
        <fieldset disabled={demonstrando} className="grupo-controle transbordo-cenario">
          <SeletorProduto
            idSelecionado={idProduto}
            aoSelecionar={(id) => {
              setIdProduto(id)
              setPassos([])
              historico.limpar()
            }}
          />
          <span className="titulo-controle">Pose do tanque avariado</span>
          <div className="seletor-produto" role="radiogroup" aria-label="Pose do tanque">
            {(Object.keys(POSES) as IdPose[]).map((id) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={pose.pose === id}
                className={`btn-acao-cad ${pose.pose === id ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={() => setPose({ ...pose, pose: id })}
              >
                {POSES[id].nome}
              </button>
            ))}
          </div>
          <label className="deslizante">
            <span>Inclinação do terreno <strong>{pose.arfagemGraus}°</strong> ({pose.arfagemGraus > 0 ? 'frente mais baixa' : pose.arfagemGraus < 0 ? 'traseira mais baixa' : 'plano'})</span>
            <input type="range" min={-12} max={12} step={1} value={pose.arfagemGraus} onChange={(e) => setPose({ ...pose, arfagemGraus: Number(e.target.value) })} />
          </label>
          <label className="deslizante">
            <span>Enchimento <strong>{Math.round(enchimento * 100)}%</strong> · {fmt(volumeTanqueM3(tanque) * enchimento)} m³ de líquido</span>
            <input type="range" min={0.05} max={0.95} step={0.05} value={enchimento} onChange={(e) => setEnchimento(Number(e.target.value))} />
          </label>
          <label className="deslizante">
            <span>Transparência do tanque <strong>{Math.round((1 - opacidade) * 100)}%</strong></span>
            <input type="range" min={0.15} max={1} step={0.05} value={opacidade} onChange={(e) => setOpacidade(Number(e.target.value))} />
          </label>
        </fieldset>
      ),
    },
    {
      id: 'receptor',
      rotulo: 'Receptor',
      icone: <IconePosicao />,
      ativa: posicionando,
      resumo: `${fmt(posicao.distanciaM)} m, ${posicao.setor === 'aFavor' ? 'a favor do vento' : posicao.setor === 'contra' ? 'contra o vento' : 'de lado'}`,
      conteudo: () => (
        <fieldset disabled={demonstrando} className="grupo-controle transbordo-cenario">
          <button
            type="button"
            aria-pressed={posicionando}
            className={`btn-acao-cad ${posicionando ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
            onClick={() => setPosicionando((v) => !v)}
          >
            {posicionando ? 'Toque no chão para estacionar o receptor' : 'Posicionar o receptor no chão'}
          </button>
          <dl className="leituras">
            <div><dt>Distância ao avariado</dt><dd>{fmt(posicao.distanciaM)} m</dd></div>
            <div><dt>Em relação ao vento</dt><dd>{posicao.setor === 'aFavor' ? 'A FAVOR do vento' : posicao.setor === 'contra' ? 'contra o vento' : 'de lado'}</dd></div>
            <div><dt>Mangote de {COMPRIMENTO_MANGOTE_M} m alcança?</dt><dd>{posicao.alcancaMangote ? 'sim' : 'não'}</dd></div>
          </dl>
          <span className="titulo-controle">Vento — de onde vem</span>
          <RosaDosVentos direcaoGraus={direcaoVento} aoMudar={setDirecaoVento} />
        </fieldset>
      ),
    },
    {
      id: 'procedimento',
      rotulo: 'Procedimento',
      icone: <IconeProcedimentos />,
      badge: avaliacao.erros > 0 ? String(avaliacao.erros) : undefined,
      conteudo: () => (
        <div className="grupo-controle">
          <button type="button" className="btn-acao-cad btn-acao-cad--primario" onClick={iniciarDemo}>
            {demonstrando ? 'Repetir animação completa' : 'Animar procedimento completo'}
          </button>
          <p className="nota-controle">Demonstração: tanque {poseDemo.pose === 'tombadoDireito' ? 'tombado sobre o lado direito' : 'sobre rodas'}, 80% de enchimento e receptor contra o vento. Ao sair, seu exercício é restaurado.</p>
          <span className="titulo-controle">Próxima ação</span>
          <fieldset disabled={demonstrando} className="transbordo-cenario"><BotoesProcedimento estado={produto.estadoTransporte} aberturas={tanque.aberturas} aoExecutar={executar} /></fieldset>
          <RegistroProcedimento avaliacao={avaliacao} aoReiniciar={demonstrando ? iniciarDemo : reiniciar} />
        </div>
      ),
    },
  ]

  const conexaoLiquido = passoCorreto('conectarMangoteLiquido')
  const conexaoVapor = passoCorreto('conectarRetornoVapor')
  const pontoLiquido = conexaoLiquido?.idAbertura ? noMundoAvariado(conexaoLiquido.idAbertura) : null
  const pontoVapor = conexaoVapor?.idAbertura ? noMundoAvariado(conexaoVapor.idAbertura) : null

  return (
    <CascaOficina
      contexto={contexto}
      oficina={{
        volume: VOLUME,
        recolherFerramentas: demonstrando,
        distanciaMaxima: 150,
        corFundo: '#26333f',
        neblinaM: [200, 700],
        idSelecionado: parte ? chaveParte(parte) : null,
        nomeSelecionado: inspetor?.titulo ?? null,
        aoClicarVazio: () => setParte(null),
        historico,
        ferramentas: {
          grupos,
          chips: [
            { id: 'pose', rotulo: 'Pose', valor: POSES[pose.pose].nome },
            { id: 'enchimento', rotulo: 'Enchimento', valor: `${Math.round(enchimento * 100)}%` },
            { id: 'receptor', rotulo: 'Receptor', valor: posicao.setor === 'aFavor' ? 'a favor do vento' : 'ok', tom: posicao.setor === 'aFavor' ? 'perigo' : 'sucesso' },
            { id: 'progresso', rotulo: 'Ações', valor: `${feitas.size} feitas · ${avaliacao.erros} erro(s)`, tom: avaliacao.erros ? 'alerta' : 'neutro' },
          ],
          inspetor,
        },
        operacao: (
          <div className="ficha-produto">
            <section>
              <h3 className="titulo-controle">Aberturas nesta pose</h3>
              <table className="tabela-resultado">
                <thead><tr><th>Abertura</th><th>Fase</th><th className="numero">Do chão</th></tr></thead>
                <tbody>
                  {situacoes.map((s) => (
                    <tr key={s.abertura.id}>
                      <td><button type="button" className="ligacao" onClick={() => setParte({ tipo: 'abertura', id: s.abertura.id })}>{s.abertura.nome}</button></td>
                      <td>
                        <span className={`selo-fase selo-fase--${s.obstruida ? 'obstruida' : s.fase}`}>
                          {s.obstruida ? 'prensada no solo' : s.fase === 'liquido' ? 'líquido' : 'vapor'}
                        </span>
                      </td>
                      <td className="numero">{fmt(s.alturaSoloM, 2)} m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="nota-controle">
                A superfície do líquido fica sempre horizontal; o tanque é que gira. Uma válvula "de líquido" pode estar no
                vapor depois do tombamento.
              </p>
            </section>
          </div>
        ),
        checklist: <RegistroProcedimento avaliacao={avaliacao} aoReiniciar={demonstrando ? iniciarDemo : reiniciar} />,
        itensEstado: [
          { id: 'vento', rotulo: 'Vento', valor: `de ${pontoCardeal(direcaoVento)}` },
          ...(posicionando ? [{ id: 'modo', rotulo: 'Modo', valor: 'posicionando receptor', tom: 'info' as const }] : []),
        ],
        sobreposicao: demonstrando ? (
          <div className="transbordo-demonstracao">
            <div role="status"><strong>Etapa {passos.length}/{sequenciaDemo.length} · {acaoPorId(passos[passos.length - 1]!.acao).nome}</strong>
              <p>{acaoPorId(passos[passos.length - 1]!.acao).porque}</p>
              <small>Verde: aterramento/equipotencialização · Azul: líquido → receptor · Amarelo: vapor → avariado. Fluxo ilustrativo.</small>
            </div>
            <div className="transbordo-demonstracao__controles">
              <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => setReproduzindo((v) => !v)}>{reproduzindo ? 'Pausar animação' : 'Continuar animação'}</button>
              <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={avancarDemo} disabled={passos.length >= sequenciaDemo.length}>Próxima etapa</button>
              <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={historico.desfazer}>Voltar ao exercício</button>
            </div>
          </div>
        ) : posicionando ? (
          <div className="tt-banner-posicionamento" role="status">
            Toque no chão para estacionar o caminhão receptor.
            <button type="button" onClick={() => setPosicionando(false)}>Cancelar</button>
          </div>
        ) : undefined,
        cena: (
          <>
            <Terreno3D
              ladoM={1000}
              aoClicar={
                posicionando
                  ? ([x, z]) => {
                      historico.registrar()
                      setReceptor([x, z])
                      setPosicionando(false)
                    }
                  : undefined
              }
            />
            <Pista3D comprimentoM={600} />
            <Biruta3D posicao={[-10, 14]} direcaoVentoGraus={direcaoVento} velocidadeMS={3} />

            {/* avariado: gira em torno do eixo do tanque, apoiado no chão quando tombado */}
            <group position={[AVARIADO[0], eixoY, AVARIADO[1] + tanque.zCentroM]} rotation={[(pose.arfagemGraus * Math.PI) / 180, 0, (rolagem * Math.PI) / 180]}>
              <group position={[0, -tanque.alturaEixoM, -tanque.zCentroM]}>
                <CaminhaoTanque3D
                  perfil={perfil}
                  numeroRisco={produto.numeroRisco.valor}
                  onu={produto.onu}
                  classes={[produto.classe.valor, ...produto.riscosSubsidiarios]}
                  opacidadeTanque={opacidade}
                  nivelLiquidoMundoY={nivelMundoY}
                  chaveSelecionada={parte ? chaveParte(parte) : null}
                  aoSelecionar={setParte}
                />
              </group>
            </group>
            {situacoes
              .filter((s) => s.abertura.tipo !== 'bocaVisita' || tanque.compartimentos === 1)
              .map((s) => {
                const p = noMundoAvariado(s.abertura.id)!
                return <MarcadorFase3D key={s.abertura.id} posicao={[p[0], p[1] + 0.3, p[2]]} fase={s.fase} obstruida={s.obstruida} />
              })}

            {/* receptor */}
            {(!demonstrando || feitas.has('posicionarReceptor')) && <ReceptorAnimado3D key={demonstrando ? 'demo' : 'exercicio'} posicao={receptor} animar={demonstrando} pausado={pausado}>
              <CaminhaoTanque3D perfil={perfil} numeroRisco={produto.numeroRisco.valor} onu={produto.onu} classes={[produto.classe.valor]} corCavalo="#9fb8d0" />
            </ReceptorAnimado3D>}
            {(!demonstrando || feitas.has('posicionarReceptor')) && <Rotulo3D posicao={[receptor[0], 4.5, receptor[1] + tanque.zCentroM]} texto="Receptor" subtexto={posicao.setor === 'aFavor' ? 'a favor do vento!' : undefined} variante={posicao.setor === 'aFavor' ? 'risco' : 'neutro'} fator={25} />}

            {/* aterramento, equipotencialização e mangotes, conforme o que já foi feito corretamente */}
            {feitas.has('aterrarAvariado') && (
              <>
                <HasteAterramento3D pausado={pausado} posicao={haste(AVARIADO)} />
                <Rotulo3D posicao={[haste(AVARIADO)[0], 1.3, haste(AVARIADO)[1]]} texto="Aterramento do avariado" fator={20} />
                <Cabo3D pausado={pausado} de={[haste(AVARIADO)[0], 0.58, haste(AVARIADO)[1]]} para={chassiAvariado} cor="#8fcf3a" espessura={2} grampo />
              </>
            )}
            {feitas.has('aterrarReceptor') && (
              <>
                <HasteAterramento3D pausado={pausado} posicao={haste(receptor)} />
                <Rotulo3D posicao={[haste(receptor)[0], 1.3, haste(receptor)[1]]} texto="Aterramento do receptor" fator={20} />
                <Cabo3D pausado={pausado} de={[haste(receptor)[0], 0.58, haste(receptor)[1]]} para={chassi(receptor)} cor="#8fcf3a" espessura={2} grampo />
              </>
            )}
            {feitas.has('equipotencializar') && <Cabo3D pausado={pausado} de={chassiAvariado} para={chassi(receptor)} cor="#22c55e" espessura={2.5} grampo />}
            {faisca && <Faisca3D posicao={chassiAvariado} />}
            {pontoVapor && <Cabo3D pausado={pausado} de={pontoVapor} para={noMundoReceptor('vapor')} cor="#facc15" espessura={5} fluxo={feitas.has('iniciarTransferencia')} reverso />}
            {pontoLiquido && <Cabo3D pausado={pausado} de={pontoLiquido} para={noMundoReceptor('liquido')} cor="#3b82f6" espessura={6} fluxo={feitas.has('iniciarTransferencia')} />}
          </>
        ),
      }}
    />
  )
}
